import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { Fornitor_table_interface } from '@bludata/common-interfaces';
import { AppDispatch, store } from '..';
import { articoliTemp_interface } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/interfaces/articoliTemp_interface';
import { ordiniAcquisto_interface } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/interfaces/ordiniAcquisto_interface';
import { BluAsyncDialog } from '@bludata/components';
import { CarDirParams_interface } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/components/interfaces/CarDirParams_interface';
import { parametri_ddt_interface } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/interfaces/parametri_ddt_interface';
import { opzioniCarico_interface } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/interfaces/opzioniCarico_interface';
import { openDialogAsyncSelezioneLotto } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/hooks/openDialogAsyncSelezioneLotto';
import { openDialogAsyncListaOrdini } from '@focus.cloud/src/magazzino/menu/menuGestione/caricoMerce/hooks/openDialogAsyncListaOrdini';
// Importazione del file audio per l'alert sonoro
const alertSound = '/resources/audio/alert-audio-carico.mp3';

// Helper function per generare l'oggetto filtroOrdini
const createFiltroOrdini = () => {
  const state = store.getState().carico;
  return {
    codiceFornitore: state.fornitoreListaOrdini?.codiceFiliale || '',
    descrizione: state.riferimentoListaOrdini || '',
    filiale:
      state.filialeListaOrdini === 'tutte' ? null : state.filialeListaOrdini,
    tipoMagazzino:
      state.magazzinoListaOrdini === -1 ? null : state.magazzinoListaOrdini,
  };
};

export interface CaricoSliceState {
  filialeListaOrdini: string | null;
  fornitoreListaOrdini: Fornitor_table_interface | null;
  magazzinoListaOrdini: number | null;
  riferimentoListaOrdini: string | null;
  parMovScontoVendita: number;
  parMovLotto: string;
  parMovDataScadenza: string;
  parMovDataMovimento: string;
  articoliTemp: articoliTemp_interface[];
  listaOrdiniAcquisto: ordiniAcquisto_interface[];
  carDirParams_Obj: CarDirParams_interface | null;
  parametriDDT: parametri_ddt_interface;
  opzioniCarico: opzioniCarico_interface;
  caricoInCorso: boolean;
  showCaricoMerce: boolean;
  showParametriDiCarico: boolean;
  showOperazioniDiMagazzino: boolean;
  addArticoloInCorso: boolean;
  lastAddArticoloSuccess: boolean | null;
  ultimoArticoloLetto: string;
  idOperazione: string;
  totaleGrid: number;
  totaleGridSconto: number;
  totaleArticoli: number;
}

const initialState: CaricoSliceState = {
  filialeListaOrdini: null,
  parMovScontoVendita: 0,
  parMovLotto: '',
  parMovDataScadenza: '',
  parMovDataMovimento: '',
  fornitoreListaOrdini: null,
  magazzinoListaOrdini: -1,
  riferimentoListaOrdini: null,
  articoliTemp: [],
  listaOrdiniAcquisto: [],
  carDirParams_Obj: null,
  parametriDDT: null,
  opzioniCarico: null,
  caricoInCorso: false,
  showCaricoMerce: false,
  showParametriDiCarico: false,
  showOperazioniDiMagazzino: false,
  addArticoloInCorso: false,
  lastAddArticoloSuccess: null,
  ultimoArticoloLetto: '',
  idOperazione: '',
  totaleGrid: 0,
  totaleGridSconto: 0,
  totaleArticoli: 0,
};

export const CaricoSlice = createSlice({
  name: 'carico',
  initialState,
  reducers: {
    setFilialeListaOrdini(
      state: CaricoSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.filialeListaOrdini = action.payload;
    },
    setParMovSconto(state: CaricoSliceState, action: PayloadAction<number>) {
      state.parMovScontoVendita = action.payload;
    },
    setParMovLotto(state: CaricoSliceState, action: PayloadAction<string>) {
      state.parMovLotto = action.payload;
    },
    setParMovDataScadenza(
      state: CaricoSliceState,
      action: PayloadAction<string>,
    ) {
      state.parMovDataScadenza = action.payload;
    },
    setParMovDataMovimento(
      state: CaricoSliceState,
      action: PayloadAction<string>,
    ) {
      state.parMovDataMovimento = action.payload;
    },
    setFornitoreListaOrdini(
      state: CaricoSliceState,
      action: PayloadAction<Fornitor_table_interface | null>,
    ) {
      state.fornitoreListaOrdini = action.payload;
    },
    setMagazzinoListaOrdini(
      state: CaricoSliceState,
      action: PayloadAction<number | null>,
    ) {
      state.magazzinoListaOrdini = action.payload;
    },
    setRiferimentoListaOrdini(
      state: CaricoSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.riferimentoListaOrdini = action.payload;
    },
    setArticoliTemp(
      state: CaricoSliceState,
      action: PayloadAction<articoliTemp_interface[]>,
    ) {
      state.articoliTemp = action.payload;
    },
    setListaOrdiniAcquisto(
      state: CaricoSliceState,
      action: PayloadAction<ordiniAcquisto_interface[]>,
    ) {
      state.listaOrdiniAcquisto = action.payload;
    },
    setCarDirParams(
      state: CaricoSliceState,
      action: PayloadAction<CarDirParams_interface | null>,
    ) {
      state.carDirParams_Obj = action.payload;
    },
    setParametriDDT(
      state: CaricoSliceState,
      action: PayloadAction<parametri_ddt_interface>,
    ) {
      state.parametriDDT = action.payload;
    },
    setOpzioniCarico(
      state: CaricoSliceState,
      action: PayloadAction<opzioniCarico_interface>,
    ) {
      state.opzioniCarico = action.payload;
    },
    setCaricoInCorso(state: CaricoSliceState, action: PayloadAction<boolean>) {
      state.caricoInCorso = action.payload;
    },
    setShowCaricoMerce(
      state: CaricoSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.showCaricoMerce = action.payload;
    },
    setShowParametriDiCarico(
      state: CaricoSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.showParametriDiCarico = action.payload;
    },
    setShowOperazioniDiMagazzino(
      state: CaricoSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.showOperazioniDiMagazzino = action.payload;
    },
    setAddArticoloInCorso(
      state: CaricoSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.addArticoloInCorso = action.payload;
    },
    setLastAddArticoloSuccess(
      state: CaricoSliceState,
      action: PayloadAction<boolean | null>,
    ) {
      state.lastAddArticoloSuccess = action.payload;
    },
    setUltimoArticoloLetto(
      state: CaricoSliceState,
      action: PayloadAction<string>,
    ) {
      state.ultimoArticoloLetto = action.payload;
    },
    setIdOperazione(state: CaricoSliceState, action: PayloadAction<string>) {
      state.idOperazione = action.payload;
    },
    setTotaleGrid(state: CaricoSliceState, action: PayloadAction<number>) {
      state.totaleGrid = action.payload;
    },
    setTotaleGridSconto(
      state: CaricoSliceState,
      action: PayloadAction<number>,
    ) {
      state.totaleGridSconto = action.payload;
    },
    setTotaleArticoli(state: CaricoSliceState, action: PayloadAction<number>) {
      state.totaleArticoli = action.payload;
    },
    updateSingleArticolo(
      state: CaricoSliceState,
      action: PayloadAction<articoliTemp_interface>,
    ) {
      const idx = state.articoliTemp.findIndex(
        (a) => a.id === action.payload.id,
      );
      if (idx !== -1) {
        state.articoliTemp[idx] = action.payload;
      }
    },
  },
});

export const addArticoliTemp = (
  barcode?: string | null,
  codiceOrdine?: string,
  codiceFilialeOrdineRiga?: string,
  fromListaOrdini?: boolean,
) => {
  return async (dispatch: AppDispatch) => {
    // Funzione di utilità per riprodurre il suono di alert
    const caricoAlert = () => {
      if (!store.getState().carico.opzioniCarico?.avvisiErrore) {
        return;
      }
      const audio = new Audio(alertSound);
      audio
        .play()
        .catch((error) =>
          console.error('Errore nella riproduzione audio:', error),
        );
    };

    const barcodeNormalizzato = barcode?.trim();

    if (!barcodeNormalizzato) {
      return;
    }
    let procedi = true;
    let lotto = '';
    let dataScadenza = '';
    let ordini: ordiniAcquisto_interface[] = [];
    dispatch(setAddArticoloInCorso(true));
    dispatch(setLastAddArticoloSuccess(null));

    //? Chiamata di check per il barcode
    await axios
      .get(`/focusapi/carico/checkarticolo/${barcodeNormalizzato}`, {
        params: { ...createFiltroOrdini(), fromListaOrdini },
      })
      .then(async (response) => {
        const reactDialog = response.data?.reactDialogs;
        if (reactDialog?.length > 0) {
          for (const dialog of reactDialog) {
            const tipoDialog = dialog.stato;
            if (tipoDialog === 300) {
              // Lettura Lotto
              const objLotto = await openDialogAsyncSelezioneLotto();
              lotto = objLotto.lotto;
              dataScadenza = objLotto.dataScadenza || '';
            } else if (tipoDialog === 400 && dialog?.opzioni.length > 0) {
              // Selezione ordine
              ordini = await openDialogAsyncListaOrdini(dialog?.opzioni);
              //? Se null vuol dire che ho premuto annulla, in questo caso devo bloccare l'aggiunta dell'articolo
              if (ordini === null) {
                dispatch(setLastAddArticoloSuccess(false));
                dispatch(setAddArticoloInCorso(false));
                return;
              }
            } else {
              procedi = await BluAsyncDialog({
                title: dialog.title || 'Attenzione',
                message: dialog.message,
                type: dialog.type as 'error' | 'info' | 'warning',
                confirmButton: dialog.confirmButton,
                declineButton: dialog.declineButton,
              });

              if (!procedi) break;
            }
          }
        }
      })
      .catch(async (error) => {
        caricoAlert();

        await BluAsyncDialog({
          title: 'Errore',
          message: error.response?.data,
          type: 'error',
          hideDeclineButton: true,
          confirmButton: 'Ok',
        });
        dispatch(setLastAddArticoloSuccess(false));
        dispatch(setAddArticoloInCorso(false));
        return;
      });

    if (procedi) {
      // Funzione per creare la richiesta axios per un ordine specifico
      const createPostRequest = (ordine: ordiniAcquisto_interface) => {
        return axios
          .post('/focusapi/carico/addArticoloTemp', {
            requestOrdini: {
              filtroOrdini: createFiltroOrdini(),
              codiceOrdine: ordine?.codiceOrdine || codiceOrdine || null,
              codiceFilialeOrdineRiga:
                ordine?.codiceFilialeOrdineRiga ||
                codiceFilialeOrdineRiga ||
                null,
              barcode,
              quantita: ordine?.quantita || null,
              lotto: lotto ? lotto : store.getState().carico.parMovLotto || '',
              dataScadenza,
              codiceFornitore: '',
              isConfidenzialeAbiliato:
                store.getState().catalogo.confidenzialeActive,
            },
          })
          .then(async (response) => {
            if (response.data.code > 200) {
              caricoAlert();

              await BluAsyncDialog({
                title: 'Attenzione',
                message: response.data.message,
                type: 'error',
                hideDeclineButton: true,
                confirmButton: 'Ok',
              });
              dispatch(setLastAddArticoloSuccess(false));
              return;
            } else {
              const reactDialog = response.data?.reactDialog;
              if (reactDialog) {
                await BluAsyncDialog({
                  title: reactDialog.title || 'Attenzione',
                  message: reactDialog.message,
                  type: reactDialog.type as 'error' | 'info' | 'warning',
                  confirmButton: reactDialog.confirmButton,
                  declineButton: reactDialog.declineButton,
                  hideDeclineButton: reactDialog.declineButton === '',
                });
              }
            }
            dispatch(setUltimoArticoloLetto(barcode));
            dispatch(setLastAddArticoloSuccess(true));
          })
          .catch(async (error) => {
            caricoAlert();

            await BluAsyncDialog({
              title: 'Errore',
              message: error.response?.data,
              type: 'error',
              hideDeclineButton: true,
              confirmButton: 'Ok',
            });
            dispatch(setLastAddArticoloSuccess(false));
          });
      };

      // Se ci sono più ordini, eseguire la richiesta per ognuno
      if (ordini.length > 0) {
        await Promise.all(ordini.map((ordine) => createPostRequest(ordine)));
      } else {
        // Caso senza ordini, eseguire una sola chiamata
        await createPostRequest(null);
      }
    }

    dispatch(getArticoliTemp());
    dispatch(setAddArticoloInCorso(false));
  };
};

export const editArticoloTemp = (
  articolo: articoliTemp_interface,
  editArticoloTemp: ordiniAcquisto_interface[],
) => {
  return (dispatch: AppDispatch) => {
    const listaOrdiniManuale = editArticoloTemp.map((ordine) => ({
      codiceOrdine: ordine.codiceOrdine,
      codiceFilialeOrdineRiga: ordine.codiceFilialeOrdineRiga,
      quantita: ordine.quantita,
    }));

    axios
      .put('/focusapi/carico/editArticoloTemp', {
        articolotemp: articolo,
        filtroOrdini: createFiltroOrdini(),
        listaOrdiniManuale:
          editArticoloTemp.length > 0 ? listaOrdiniManuale : [],
      })
      .then((response) => {
        dispatch(getArticoliTemp());
      })
      .catch((error) => {
        BluAsyncDialog({
          title: 'Attenzione',
          message:
            error.response?.data || "Errore durante la modifica dell'articolo",
          type: 'error',
          confirmButton: 'Chiudi',
          hideDeclineButton: true,
        });
      });
  };
};

/**
 * Ricarica la lista articoli e i totali senza mostrare il loader,
 * poi sincronizza la lista ordini. Usato dopo editing inline.
 */
const refreshArticoliSilent = () => {
  return async (dispatch: AppDispatch) => {
    const response = await axios.get('/focusapi/carico/articoliTemp');
    dispatch(setArticoliTemp(response.data.articoli));
    dispatch(setTotaleGrid(response.data.totale));
    dispatch(setTotaleGridSconto(response.data.totaleScontato));
    dispatch(setTotaleArticoli(response.data?.totaleArticoli || 0));
    dispatch(getDettaglioOrdiniAcquisto());
  };
};

/**
 * Versione inline di editArticoloTemp: salva la riga tramite PUT,
 * poi ricarica la lista completa (senza loader) per gestire
 * eventuali righe create/modificate dal backend (es. split quantità).
 */
export const editArticoloTempInline = (
  articolo: articoliTemp_interface,
  listaOrdini: ordiniAcquisto_interface[],
) => {
  return async (dispatch: AppDispatch) => {
    const listaOrdiniManuale = listaOrdini.map((ordine) => ({
      codiceOrdine: ordine.codiceOrdine,
      codiceFilialeOrdineRiga: ordine.codiceFilialeOrdineRiga,
      quantita: ordine.quantita,
    }));

    dispatch(setAddArticoloInCorso(true));
    try {
      await axios.put('/focusapi/carico/editArticoloTemp', {
        articolotemp: articolo,
        filtroOrdini: createFiltroOrdini(),
        listaOrdiniManuale: listaOrdini.length > 0 ? listaOrdiniManuale : [],
      });

      // Ricarica tutta la lista articoli + ordini
      await dispatch(refreshArticoliSilent());
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: string } };
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          axiosError.response?.data ||
          "Errore durante la modifica dell'articolo",
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
    } finally {
      dispatch(setAddArticoloInCorso(false));
    }
  };
};

export const deleteArticoliTempById = (id: number) => {
  return (dispatch: AppDispatch) => {
    axios
      .delete(`/focusapi/carico/removeArticoloTemp/${id}`)
      .then((response) => {
        dispatch(getArticoliTemp());
      })
      .catch((error) => {
        BluAsyncDialog({
          title: 'Attenzione',
          message:
            error.response?.data ||
            "Errore durante l'eliminazione dell'articolo",
          type: 'error',
          confirmButton: 'Chiudi',
          hideDeclineButton: true,
        });
      });
  };
};

export const validateQuantita = (articolo: articoliTemp_interface) => {
  //? Funzione che ricalcola i prezzi a seconda della quantità inserita dall'utente
  return axios
    .put(`/focusapi/carico/${articolo.id}/validate/quantita`, {
      articolotemp: articolo,
      filtroOrdini: createFiltroOrdini(),
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          error.response?.data ||
          'Errore durante la validazione della quantità',
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
      throw error;
    });
};

export const checkEditListaOrdini = (articolo: articoliTemp_interface) => {
  //? Dovrò salvare quello che l'utente ha fatto per poi passarlo al salvataggio della riga
  return axios
    .get(`/focusapi/carico/${articolo.id}/listaOrdini`, {
      params: { ...createFiltroOrdini(), quantita: articolo.quantita },
    })
    .then(async (response) => {
      const reactDialog = response.data?.reactDialog;
      let ordini: ordiniAcquisto_interface[] = [];
      if (reactDialog) {
        const tipoDialog = reactDialog.stato;
        if (tipoDialog === 400 && reactDialog?.opzioni.length > 0) {
          // Selezione ordine
          ordini = await openDialogAsyncListaOrdini(reactDialog?.opzioni);
          //? Se null vuol dire che ho premuto annulla, in questo caso devo bloccare l'aggiunta dell'articolo
        }
      }
      return ordini;
    });
};

export const validatePrezzoVendita = (articolo: articoliTemp_interface) => {
  return axios
    .put(`/focusapi/carico/${articolo.id}/validate/prezzoVendita`, {
      articolotemp: articolo,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          error.response?.data ||
          'Errore durante la validazione del prezzo vendita',
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
      throw error;
    });
};

export const validateScontoVendita = (articolo: articoliTemp_interface) => {
  return axios
    .put(`/focusapi/carico/${articolo.id}/validate/scontoVendita`, {
      articolotemp: articolo,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          error.response?.data ||
          'Errore durante la validazione dello sconto vendita',
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
      throw error;
    });
};

export const validatePrezzoScontato = (articolo: articoliTemp_interface) => {
  return axios
    .put(`/focusapi/carico/${articolo.id}/validate/prezzoScontato`, {
      articolotemp: articolo,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          error.response?.data ||
          'Errore durante la validazione del prezzo scontato',
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
      throw error;
    });
};

export const validateScontoAcquisto = (articolo: articoliTemp_interface) => {
  return axios
    .put(`/focusapi/carico/${articolo.id}/validate/scontoAcquisto`, {
      articolotemp: articolo,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      BluAsyncDialog({
        title: 'Attenzione',
        message:
          error.response?.data ||
          'Errore durante la validazione dello sconto acquisto',
        type: 'error',
        confirmButton: 'Chiudi',
        hideDeclineButton: true,
      });
      throw error;
    });
};

export const deleteArticoliTempByOrder = (
  codiceOrdine: string,
  codiceFilialeOrdineRiga: string,
) => {
  return (dispatch: AppDispatch) => {
    axios
      .delete(
        `/focusapi/carico/removeArticoloTempByOrdine/${codiceOrdine}/${codiceFilialeOrdineRiga}`,
      )
      .then((response) => {
        dispatch(getArticoliTemp());
      });
  };
};

export const getArticoliTemp = () => {
  return (dispatch: AppDispatch) => {
    dispatch(setAddArticoloInCorso(true));
    axios
      .get('/focusapi/carico/articoliTemp')
      .then((response) => {
        dispatch(setArticoliTemp(response.data.articoli));
        dispatch(setTotaleGrid(response.data.totale));
        dispatch(setTotaleGridSconto(response.data.totaleScontato));
        dispatch(setTotaleArticoli(response.data?.totaleArticoli || 0));
      })
      .then(() => {
        dispatch(getDettaglioOrdiniAcquisto());
      })
      .finally(() => {
        dispatch(setAddArticoloInCorso(false));
      });
  };
};

let lastRequestController: AbortController | null = null;

export const getDettaglioOrdiniAcquisto = () => {
  return (dispatch: AppDispatch) => {
    // Se c'è una richiesta precedente, annulla
    if (lastRequestController) {
      lastRequestController.abort();
    }

    // Crea un nuovo AbortController
    lastRequestController = new AbortController();
    const signal = lastRequestController.signal;
    const currentController = lastRequestController; // Mantieni riferimento al controller corrente

    axios
      .get('/focusapi/carico/dettaglioOrdiniAcquisto', {
        params: createFiltroOrdini(),
        signal, // Passa il segnale di abort alla richiesta
      })
      .then((response) => {
        // Controlla se questa è ancora la richiesta più recente e non è stata annullata
        if (currentController === lastRequestController && !signal.aborted) {
          dispatch(setListaOrdiniAcquisto(response.data));
        }
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          console.log('Richiesta annullata:', error.message);
        } else {
          console.error('Errore nella richiesta:', error);
        }
      })
      .finally(() => {
        // Resetta il controller solo se è ancora quello corrente
        if (currentController === lastRequestController) {
          lastRequestController = null;
        }
      });
  };
};

export const getMagazziniOrdineAcquisto = () => {
  return (dispatch: AppDispatch) => {
    axios.delete('/focusapi/carico/deleteAllArticoliTemp').then((response) => {
      dispatch(getArticoliTemp());
    });
  };
};

export const caricoCompattaRighe = () => {
  return (dispatch: AppDispatch) => {
    dispatch(setAddArticoloInCorso(true));
    axios
      .post('/focusapi/carico/compattaRighe')
      .then((response) => {
        dispatch(getArticoliTemp());
      })
      .catch((error) => {
        BluAsyncDialog({
          title: 'Errore',
          message:
            error.response?.data ||
            'Errore durante il compattamento delle righe',
          type: 'warning',
          hideDeclineButton: true,
          confirmButton: 'Ok',
        });
      })
      .finally(() => {
        dispatch(setAddArticoloInCorso(false));
      });
  };
};

export const resetFilterDialogCarico = () => {
  return (dispatch: AppDispatch) => {
    dispatch(setFilialeListaOrdini(null));
    dispatch(setParMovSconto(0));
    dispatch(setParMovLotto(''));
    dispatch(setParMovDataScadenza(''));
    dispatch(setFornitoreListaOrdini(null));
    dispatch(setMagazzinoListaOrdini(null));
    dispatch(setRiferimentoListaOrdini(null));
    dispatch(setParametriDDT(null));
  };
};

export const getParametriDDT = () => {
  return (dispatch: AppDispatch) => {
    axios.get(`/focusapi/carico/parametriDDT`).then((response) => {
      dispatch(setParametriDDT(response.data));
    });
  };
};

// Action creators are generated for each case reducer function
export const {
  setFilialeListaOrdini,
  setParMovSconto,
  setParMovLotto,
  setParMovDataScadenza,
  setParMovDataMovimento,
  setFornitoreListaOrdini,
  setMagazzinoListaOrdini,
  setRiferimentoListaOrdini,
  setArticoliTemp,
  setListaOrdiniAcquisto,
  setCarDirParams,
  setParametriDDT,
  setOpzioniCarico,
  setCaricoInCorso,
  setShowCaricoMerce,
  setAddArticoloInCorso,
  setLastAddArticoloSuccess,
  setUltimoArticoloLetto,
  setShowParametriDiCarico,
  setShowOperazioniDiMagazzino,
  setIdOperazione,
  setTotaleGrid,
  setTotaleGridSconto,
  setTotaleArticoli,
  updateSingleArticolo,
} = CaricoSlice.actions;

export default CaricoSlice.reducer;
